import clsx from 'clsx';
import Image from 'next/image';
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

export function Container({
  className,
  children,
  as: Tag = 'div',
}: {
  className?: string;
  children: ReactNode;
  as?: ElementType;
}) {
  return (
    <Tag className={clsx('mx-auto w-full max-w-[1240px] px-5 sm:px-8', className)}>{children}</Tag>
  );
}

/** A rounded, inset frame. The page is a stack of these on a frost background. */
export function Frame({
  className,
  children,
  as: Tag = 'div',
  ...rest
}: ComponentPropsWithoutRef<'div'> & { as?: ElementType; children: ReactNode }) {
  return (
    <div className="px-2.5 sm:px-4">
      <Tag
        {...rest}
        className={clsx('mx-auto w-full max-w-[1480px] overflow-hidden rounded-[1.75rem] sm:rounded-[2.25rem]', className)}
      >
        {children}
      </Tag>
    </div>
  );
}

type ButtonVariant = 'beacon' | 'white' | 'ink' | 'quiet-ink' | 'quiet-white';

const buttonStyles: Record<ButtonVariant, string> = {
  beacon: 'bg-beacon-500 text-white hover:bg-beacon-600 active:bg-beacon-700',
  white: 'bg-white text-ink-900 hover:bg-frost-100 active:bg-frost-200',
  ink: 'bg-ink-900 text-white hover:bg-ink-800 active:bg-ink-950',
  'quiet-ink':
    'bg-transparent text-ink-900 border border-ink-900/20 hover:border-ink-900 hover:bg-ink-900/[0.04]',
  'quiet-white':
    'bg-transparent text-white border border-white/25 hover:border-white hover:bg-white/[0.06]',
};

export function Button({
  variant = 'beacon',
  className,
  children,
  ...rest
}: ComponentPropsWithoutRef<'button'> & { variant?: ButtonVariant }) {
  return (
    <button
      {...rest}
      className={clsx(
        'inline-flex min-h-12 cursor-pointer items-center justify-center rounded-full px-6 text-[0.95rem] font-semibold tracking-[-0.005em] transition-[background-color,border-color,transform] duration-200 ease-out active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60',
        buttonStyles[variant],
        className,
      )}
    >
      {children}
    </button>
  );
}

export function SectionLabel({
  children,
  tone = 'ink',
  className,
}: {
  children: ReactNode;
  tone?: 'ink' | 'white';
  className?: string;
}) {
  return (
    <p
      className={clsx(
        'text-sm font-semibold',
        tone === 'ink' ? 'text-beacon-600' : 'text-beacon-400',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function SectionHeading({
  id,
  label,
  title,
  intro,
  tone = 'ink',
  className,
  size = 'md',
}: {
  id?: string;
  label?: string;
  title: string;
  intro?: string;
  tone?: 'ink' | 'white';
  className?: string;
  size?: 'md' | 'lg';
}) {
  return (
    <div className={clsx('max-w-[44rem]', className)}>
      {label ? (
        <SectionLabel tone={tone} className="mb-4">
          {label}
        </SectionLabel>
      ) : null}
      <h2
        id={id}
        className={clsx(
          'font-display text-balance leading-[1.02] font-bold tracking-[-0.03em]',
          size === 'lg'
            ? 'text-[clamp(2.5rem,5.2vw,4.25rem)]'
            : 'text-[clamp(2.125rem,4.2vw,3.375rem)]',
          tone === 'ink' ? 'text-ink-900' : 'text-white',
        )}
      >
        {title}
      </h2>
      {intro ? (
        <p
          className={clsx(
            'mt-5 max-w-[36rem] text-pretty text-lg leading-relaxed',
            tone === 'ink' ? 'text-muted' : 'text-mist',
          )}
        >
          {intro}
        </p>
      ) : null}
    </div>
  );
}

/** The RoosterBeacon mark. Gradient on light surfaces, white on dark. */
export function Logo({
  tone = 'light',
  className,
  wordmark = true,
}: {
  tone?: 'light' | 'dark';
  className?: string;
  wordmark?: boolean;
}) {
  return (
    <span className={clsx('inline-flex items-center gap-2.5', className)}>
      <Image
        src={tone === 'dark' ? '/logo-dark.svg' : '/logo.svg'}
        alt=""
        width={32}
        height={32}
        className="h-8 w-8"
        priority
        unoptimized
      />
      {wordmark ? (
        <span
          className={clsx(
            'font-display text-[1.2rem] leading-none font-bold tracking-[-0.02em]',
            tone === 'dark' ? 'text-white' : 'text-ink-900',
          )}
        >
          RoosterBeacon
        </span>
      ) : null}
    </span>
  );
}
